/**
 * StorageHelper
 * - Provides safe, convenient wrappers for localStorage operations.
 * - Handles string and object/array values automatically (stringifies/JSON parses).
 * - Catches and logs errors for all storage operations.
 * - Used throughout the app for persisting tokens, user info, and settings.
 */
export const StorageHelper = {
  /**
   * Stores a value in localStorage.
   * - Automatically stringifies objects/arrays.
   * - Stores strings as-is.
   * @param {string} key
   * @param {any} value
   */
  set: (key, value) => {
    try {
      // If value is already a string, store directly
      if (typeof value === 'string') {
        localStorage.setItem(key, value);
      } else {
        // For objects/arrays, stringify
        const stringValue = JSON.stringify(value);
        localStorage.setItem(key, stringValue);
      }
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  },

  /**
   * Retrieves a value from localStorage.
   * - Tries to parse as JSON, falls back to string if parsing fails.
   * @param {string} key
   * @returns {any|null}
   */
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      // Try parsing as JSON, if fails return the raw string
      try {
        return JSON.parse(item);
      } catch (e) {
        // If parsing fails, it's probably a token or simple string
        return item;
      }
    } catch (e) {
      console.error('Error reading from localStorage:', e);
      return null;
    }
  },

  /**
   * Removes a value from localStorage.
   * @param {string} key
   */
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error('Error removing from localStorage:', e);
    }
  },

  /**
   * Clears all localStorage.
   */
  clear: () => {
    try {
      localStorage.clear();
    } catch (e) {
      console.error('Error clearing localStorage:', e);
    }
  },

  /**
   * Checks if a string is valid JSON.
   * @param {string} str
   * @returns {boolean}
   */
  isJSON: (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }
};
